// src/api/appConfigs/appConfigs.ts
import { useApiClient } from "../apiClients";
import { APP_CONFIGS_ENDPOINTS } from "./endpoints";
import type {
  AccessToken,
  AppConfig,
  FactPromptsResponse,
  LlmCatalog,
  LlmCostEstimates,
  LlmProvider,
  ModelDeclaration,
  QuestPromptsResponse,
  TilmidPlusConfig,
} from "../../models/domain";

export const useAppConfigsApi = () => {
  const apiClient = useApiClient("embed");
  // Les prompts des quêtes appartiennent à tc_learn : appelé par le broker,
  // comme « Quêtes à corriger ».
  const learnClient = useApiClient("learn");

  const getTilmidPlusConfig = () =>
    apiClient.get<AppConfig<TilmidPlusConfig>>(APP_CONFIGS_ENDPOINTS.TILMID_PLUS);

  const updateTilmidPlusConfig = (payload: TilmidPlusConfig) =>
    apiClient.put<AppConfig<TilmidPlusConfig>>(APP_CONFIGS_ENDPOINTS.TILMID_PLUS, payload);

  const getLlmCatalog = () => apiClient.get<LlmCatalog>(APP_CONFIGS_ENDPOINTS.LLM_CATALOG);

  const putLlmProvider = (id: string, provider: LlmProvider) =>
    apiClient.put<LlmCatalog>(APP_CONFIGS_ENDPOINTS.LLM_PROVIDER(id), provider);

  const declareLlmModel = (declaration: ModelDeclaration) =>
    apiClient.post<LlmCatalog>(APP_CONFIGS_ENDPOINTS.LLM_MODELS, declaration);

  const getFactPrompts = () => apiClient.get<FactPromptsResponse>(APP_CONFIGS_ENDPOINTS.FACT_PROMPTS);

  const getCostEstimates = () => apiClient.get<LlmCostEstimates>(APP_CONFIGS_ENDPOINTS.COST_ESTIMATES);

  const getQuestPrompts = () => learnClient.get<QuestPromptsResponse>(APP_CONFIGS_ENDPOINTS.QUEST_PROMPTS);

  const listAccessTokens = () => apiClient.get<{ tokens: AccessToken[] }>(APP_CONFIGS_ENDPOINTS.ACCESS_TOKENS);

  /** Le jeton (`token`) n'est rendu qu'ici, une fois : il n'est gardé que dans la fenêtre qui l'affiche. */
  const createAccessToken = (label: string, days: number) =>
    apiClient.post<{ token: string; access_token: AccessToken }>(APP_CONFIGS_ENDPOINTS.ACCESS_TOKENS, { label, days });

  const revokeAccessToken = (id: string) => apiClient.delete<void>(APP_CONFIGS_ENDPOINTS.ACCESS_TOKEN(id));

  return {
    getTilmidPlusConfig,
    updateTilmidPlusConfig,
    getLlmCatalog,
    putLlmProvider,
    declareLlmModel,
    getFactPrompts,
    getCostEstimates,
    getQuestPrompts,
    listAccessTokens,
    createAccessToken,
    revokeAccessToken,
  };
};
